"use client";

import { useEffect, useState } from "react";
import { useAudienceLens } from "@/lib/audienceLens/AudienceLensContext";

/**
 * O4 — Build-version chip. Tiny corner readout showing the backend
 * build SHA + scenarios loadedAt timestamp. Catches the "did the
 * scenario JSON edit actually deploy?" silent-failure mode that ate
 * an evening of debugging in an earlier session.
 *
 * Visibility: ALL lenses except `external`. External audiences see
 * the public demo without the ops chrome; everyone else (banker /
 * investor / regulator / enterprise) gets the chip because they're
 * already on a non-public surface.
 */
type HealthPayload = {
  ok:          boolean;
  buildSha?:   string;
  loadedAt?:   string;
  hotReload?:  boolean;
  error?:      string;
};

export function BuildVersionChip() {
  const lens = useAudienceLens();
  const [health, setHealth] = useState<HealthPayload | null>(null);

  useEffect(() => {
    if (lens === "external") return;
    let cancelled = false;
    const load = async () => {
      try {
        const r = await fetch("/api/healthz", { headers: { Accept: "application/json" } });
        if (!r.ok) {
          if (!cancelled) setHealth({ ok: false, error: `${r.status} ${r.statusText}` });
          return;
        }
        const data = (await r.json()) as HealthPayload;
        if (!cancelled) setHealth(data);
      } catch (err) {
        if (!cancelled) setHealth({ ok: false, error: err instanceof Error ? err.message : String(err) });
      }
    };
    void load();
    // Re-poll every 60s — picks up hot-reloads done since the page
    // loaded without forcing a refresh.
    const id = window.setInterval(load, 60_000);
    return () => { cancelled = true; window.clearInterval(id); };
  }, [lens]);

  if (lens === "external") return null;
  if (!health) return null;

  const loadedAt = health.loadedAt
    ? new Date(health.loadedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false })
    : null;

  return (
    <div
      data-build-chip
      className="pointer-events-none fixed bottom-3 start-3 z-40 select-none rounded border border-orbitra-muted/30 bg-orbitra-ink/85 px-2 py-1 font-mono text-[9px] text-orbitra-muted shadow-glass"
      title={
        health.ok
          ? `Backend build ${health.buildSha ?? "?"} · scenarios loaded at ${health.loadedAt ?? "?"} · hot-reload ${health.hotReload ? "on" : "off"}`
          : `Backend unreachable · ${health.error ?? "unknown error"}`
      }
    >
      {health.ok ? (
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-policy-pass" aria-hidden="true" />
          <span>build {(health.buildSha ?? "?").slice(0, 7)}</span>
          {loadedAt && <span className="text-orbitra-muted/70">· loaded {loadedAt}</span>}
        </span>
      ) : (
        <span className="flex items-center gap-1.5 text-policy-fail">
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-policy-fail" aria-hidden="true" />
          <span>backend unreachable</span>
        </span>
      )}
    </div>
  );
}
